'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Props {
  onResultsChange: (count: number) => void;
}

const propertyTypes = ['Apartment', 'Villa', 'Penthouse', 'Townhouse', 'Studio', 'Duplex', 'Office', 'Retail', 'Warehouse', 'Land'];
const locations = ['Dubai Marina', 'Palm Jumeirah', 'Downtown Dubai', 'Business Bay', 'Jumeirah', 'JBR', 'DIFC', 'Al Reem Island', 'Saadiyat Island', 'Yas Island', 'The Pearl – Qatar', 'KAEC', 'Riyadh Hills'];
const amenitiesList = [
  'Swimming Pool', 'Private Pool', 'Gym', 'Parking', 'Balcony', 'Sea View', 'Burj Khalifa View',
  'Maid Room', 'Study Room', 'Private Garden', 'Smart Home', 'Central A/C', 'BBQ Area',
  'Beach Access', 'Golf Course View', 'Tennis Court', 'Sauna', 'Children\'s Play Area',
  'Concierge Service', 'Security 24/7', 'Backup Generator', 'EV Charging', 'Storage Room',
  'Home Cinema', 'Wine Cellar', 'Driver Room', 'Laundry Room', 'Covered Parking',
];
const furnishingOptions = ['Any', 'Furnished', 'Unfurnished', 'Semi-Furnished'];
const completionOptions = ['Any', 'Ready', 'Off-Plan', 'Under Construction', 'Ready Soon'];
const bedroomOptions = ['Any', 'Studio', '1', '2', '3', '4', '5', '6', '7+'];

export default function AdvancedFiltersPanel({ onResultsChange }: Props) {
  const [keyword, setKeyword] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(50000000);
  const [minArea, setMinArea] = useState(0);
  const [maxArea, setMaxArea] = useState(20000);
  const [bedrooms, setBedrooms] = useState('Any');
  const [bathrooms, setBathrooms] = useState('Any');
  const [furnishing, setFurnishing] = useState('Any');
  const [completion, setCompletion] = useState('Any');
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [dldVerified, setDldVerified] = useState(false);
  const [freeholdOnly, setFreeholdOnly] = useState(false);
  const [listingType, setListingType] = useState('all');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['keyword', 'type', 'location', 'price', 'area', 'beds', 'amenities', 'flags'])
  );

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
    onResultsChange(Math.floor(Math.random() * 20) + 5);
  };

  const toggleLocation = (loc: string) => {
    setSelectedLocations(prev =>
      prev.includes(loc) ? prev.filter(l => l !== loc) : [...prev, loc]
    );
    onResultsChange(Math.floor(Math.random() * 20) + 5);
  };

  const toggleAmenity = (a: string) => {
    setSelectedAmenities(prev =>
      prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]
    );
  };

  const clearAll = () => {
    setKeyword('');
    setSelectedTypes([]);
    setSelectedLocations([]);
    setMinPrice(0);
    setMaxPrice(50000000);
    setMinArea(0);
    setMaxArea(20000);
    setBedrooms('Any');
    setBathrooms('Any');
    setFurnishing('Any');
    setCompletion('Any');
    setSelectedAmenities([]);
    setDldVerified(false);
    setFreeholdOnly(false);
    setListingType('all');
    onResultsChange(8);
  };

  const Section = ({ id, title, count, children }: { id: string; title: string; count?: number; children: React.ReactNode }) => (
    <div className="border-b border-border pb-4 mb-4 last:border-0 last:pb-0 last:mb-0">
      <button
        onClick={() => toggleSection(id)}
        className="flex items-center justify-between w-full text-sm font-semibold text-foreground mb-3 hover:text-primary transition-colors"
      >
        <div className="flex items-center gap-2">
          {title}
          {count !== undefined && count > 0 && (
            <span className="w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center font-bold">
              {count}
            </span>
          )}
        </div>
        <Icon
          name={expandedSections.has(id) ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={15}
          className="text-muted-foreground"
        />
      </button>
      {expandedSections.has(id) && <div>{children}</div>}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-2xl shadow-card sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-hide">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
        <div className="flex items-center gap-2">
          <Icon name="AdjustmentsHorizontalIcon" size={18} className="text-primary" />
          <h3 className="font-bold text-foreground text-sm">Advanced Filters</h3>
          {(selectedTypes.length + selectedLocations.length + selectedAmenities.length) > 0 && (
            <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {selectedTypes.length + selectedLocations.length + selectedAmenities.length}
            </span>
          )}
        </div>
        <button onClick={clearAll} className="text-xs text-primary font-semibold hover:underline">
          Clear All
        </button>
      </div>

      <div className="p-5 space-y-0">
        {/* Keyword Search */}
        <Section id="keyword" title="Keyword Search">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Property name, community, developer..."
              className="w-full pl-9 pr-4 py-2.5 bg-muted border border-border rounded-xl text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </Section>

        {/* Listing Type */}
        <Section id="listingType" title="Listing Type">
          <div className="grid grid-cols-4 gap-1.5">
            {[
              { value: 'all', label: 'All' },
              { value: 'sale', label: 'Buy' },
              { value: 'rent', label: 'Rent' },
              { value: 'offplan', label: 'Off-Plan' },
            ].map((opt) => (
              <button
                key={`listing-type-${opt.value}`}
                onClick={() => setListingType(opt.value)}
                className={`py-2 rounded-lg text-xs font-semibold transition-all ${
                  listingType === opt.value
                    ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Property Type */}
        <Section id="type" title="Property Type" count={selectedTypes.length}>
          <div className="flex flex-wrap gap-1.5">
            {propertyTypes.map((type) => (
              <button
                key={`adv-type-${type}`}
                onClick={() => toggleType(type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  selectedTypes.includes(type)
                    ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </Section>

        {/* Location */}
        <Section id="location" title="Location / Community" count={selectedLocations.length}>
          <div className="space-y-1.5 max-h-52 overflow-y-auto scrollbar-hide">
            {locations.map((loc) => (
              <label key={`adv-loc-${loc}`} className="flex items-center gap-2.5 cursor-pointer group py-1">
                <div
                  onClick={() => toggleLocation(loc)}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    selectedLocations.includes(loc)
                      ? 'bg-primary border-primary' :'border-border group-hover:border-primary'
                  }`}
                >
                  {selectedLocations.includes(loc) && (
                    <Icon name="CheckIcon" size={10} className="text-white" />
                  )}
                </div>
                <span className={`text-xs transition-colors ${selectedLocations.includes(loc) ? 'text-primary font-semibold' : 'text-foreground/70 group-hover:text-foreground'}`}>
                  {loc}
                </span>
              </label>
            ))}
          </div>
        </Section>

        {/* Price Range */}
        <Section id="price" title="Price Range (AED)">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Min Price</label>
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="0"
                />
              </div>
              <span className="text-muted-foreground text-xs mt-4">—</span>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Max Price</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="50,000,000"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: 'Under 1M', min: 0, max: 1000000 },
                { label: '1M–3M', min: 1000000, max: 3000000 },
                { label: '3M–10M', min: 3000000, max: 10000000 },
                { label: '10M+', min: 10000000, max: 50000000 },
              ].map((range) => (
                <button
                  key={`price-range-${range.label}`}
                  onClick={() => { setMinPrice(range.min); setMaxPrice(range.max); }}
                  className="px-2.5 py-1 rounded-lg text-xs font-medium bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border transition-all"
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* Area Range */}
        <Section id="area" title="Area (sqft)">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Min sqft</label>
              <input
                type="number"
                value={minArea}
                onChange={(e) => setMinArea(Number(e.target.value))}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="0"
              />
            </div>
            <span className="text-muted-foreground text-xs mt-4">—</span>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Max sqft</label>
              <input
                type="number"
                value={maxArea}
                onChange={(e) => setMaxArea(Number(e.target.value))}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="20,000"
              />
            </div>
          </div>
        </Section>

        {/* Bedrooms */}
        <Section id="beds" title="Bedrooms">
          <div className="flex flex-wrap gap-1.5">
            {bedroomOptions.map((b) => (
              <button
                key={`adv-beds-${b}`}
                onClick={() => setBedrooms(b)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  bedrooms === b
                    ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                }`}
              >
                {b === 'Any' ? 'Any' : b === 'Studio' ? 'Studio' : `${b} BR`}
              </button>
            ))}
          </div>
        </Section>

        {/* Bathrooms */}
        <Section id="baths" title="Bathrooms">
          <div className="flex flex-wrap gap-1.5">
            {['Any', '1', '2', '3', '4', '5+'].map((b) => (
              <button
                key={`adv-baths-${b}`}
                onClick={() => setBathrooms(b)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  bathrooms === b
                    ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                }`}
              >
                {b === 'Any' ? 'Any' : `${b} Bath`}
              </button>
            ))}
          </div>
        </Section>

        {/* Furnishing */}
        <Section id="furnishing" title="Furnishing Status">
          <div className="flex flex-wrap gap-1.5">
            {furnishingOptions.map((f) => (
              <button
                key={`adv-furnish-${f}`}
                onClick={() => setFurnishing(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  furnishing === f
                    ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Section>

        {/* Completion Status */}
        <Section id="completion" title="Completion Status">
          <div className="flex flex-wrap gap-1.5">
            {completionOptions.map((c) => (
              <button
                key={`adv-completion-${c}`}
                onClick={() => setCompletion(c)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  completion === c
                    ? 'bg-primary text-white' :'bg-muted text-foreground/70 hover:bg-primary/10 hover:text-primary border border-border'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Section>

        {/* Amenities */}
        <Section id="amenities" title="Amenities" count={selectedAmenities.length}>
          <div className="space-y-1.5 max-h-56 overflow-y-auto scrollbar-hide">
            {amenitiesList.map((amenity) => (
              <label key={`adv-amenity-${amenity}`} className="flex items-center gap-2.5 cursor-pointer group py-0.5">
                <div
                  onClick={() => toggleAmenity(amenity)}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                    selectedAmenities.includes(amenity)
                      ? 'bg-primary border-primary' :'border-border group-hover:border-primary'
                  }`}
                >
                  {selectedAmenities.includes(amenity) && (
                    <Icon name="CheckIcon" size={10} className="text-white" />
                  )}
                </div>
                <span className={`text-xs transition-colors ${selectedAmenities.includes(amenity) ? 'text-primary font-semibold' : 'text-foreground/70 group-hover:text-foreground'}`}>
                  {amenity}
                </span>
              </label>
            ))}
          </div>
        </Section>

        {/* Verification & Ownership Flags */}
        <Section id="flags" title="Verification & Ownership">
          <div className="space-y-3">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-xs font-semibold text-foreground">DLD Verified Only</div>
                <div className="text-xs text-muted-foreground">Show only DLD permit verified listings</div>
              </div>
              <div
                onClick={() => setDldVerified(!dldVerified)}
                className={`w-10 h-5 rounded-full transition-all duration-200 relative cursor-pointer flex-shrink-0 ${dldVerified ? 'bg-primary' : 'bg-muted border border-border'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${dldVerified ? 'left-5' : 'left-0.5'}`} />
              </div>
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-xs font-semibold text-foreground">Freehold Zones Only</div>
                <div className="text-xs text-muted-foreground">Properties available for expat ownership</div>
              </div>
              <div
                onClick={() => setFreeholdOnly(!freeholdOnly)}
                className={`w-10 h-5 rounded-full transition-all duration-200 relative cursor-pointer flex-shrink-0 ${freeholdOnly ? 'bg-primary' : 'bg-muted border border-border'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${freeholdOnly ? 'left-5' : 'left-0.5'}`} />
              </div>
            </label>
          </div>
        </Section>
      </div>

      {/* Apply Button */}
      <div className="p-5 border-t border-border sticky bottom-0 bg-card">
        <button
          onClick={() => onResultsChange(Math.floor(Math.random() * 30) + 4)}
          className="w-full btn-primary flex items-center justify-center gap-2"
        >
          <Icon name="MagnifyingGlassIcon" size={16} />
          Apply Filters & Search
        </button>
      </div>
    </div>
  );
}